import { useEffect, useState, useRef } from 'react';
import { useAuth, User } from 'oidc-react';
import { useDynamicRoutes } from './useDynamicRoutes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { setAvailableBusinessUnits, setBusinessUnit } from '../store/userData/actions';
import {
  BusinessCentres,
  DESCRIBED_ROLES,
  INTERNAL_ROLES,
  Role,
  Roles,
  SapCDCRole,
} from '@cambridgeassessment/lib-ee-commons';

function useUserRoles(): Role[] {
  const { userData } = useAuth();
  let roles = userData?.profile.roles;

  if (!roles) {
    return [];
  }

  if (!Array.isArray(roles)) {
    roles = [roles];
  }

  const internalAppRoles: Roles[] =
    userData &&
    roles.flatMap((role: SapCDCRole) => role.roleNames.filter((roleName: Roles) => INTERNAL_ROLES.includes(roleName)));

  const uniqInternalAppRoles = [...new Set(internalAppRoles)];

  const internalAppFullRoles: Role[] =
    uniqInternalAppRoles &&
    uniqInternalAppRoles.map((roleName: Roles) => {
      return DESCRIBED_ROLES[roleName];
    });

  return internalAppFullRoles;
}

export const getBusinessUnits = (userData: User): BusinessCentres[] => {
  const cieBusinessObject = {
    centres: [],
    fullName: 'Cambridge Assessment International Education',
    id: '02',
    name: 'Cambridge Assessment International Education',
    shortName: 'CIE',
  };

  const ocrBusinessObject = {
    centres: [],
    fullName: 'OCR',
    id: '01',
    name: 'OCR',
    shortName: 'OCR',
  };
  if (userData.profile.businessStream === 'CSD') {
    return [cieBusinessObject, ocrBusinessObject];
  } else if (userData.profile.businessStream === 'OCR') {
    return [ocrBusinessObject];
  } else {
    return [cieBusinessObject];
  }
};

function useUserName(): string {
  const { userData } = useAuth();
  const userName = userData && userData.profile.name;

  return userName ? userName : '';
}

export const useBUData = (): void => {
  const dispatch = useDispatch();
  const { userData } = useAuth();
  const userDataOther = useSelector((state: RootState) => state.userData);
  const [businessUnits, setBusinessUnits] = useState<BusinessCentres[]>([]);

  useEffect(() => {
    if (!userData) {
      return;
    }
    setBusinessUnits(getBusinessUnits(userData));
  }, [userData]);

  if (!userData) return;

  if (userDataOther.availableBusinessUnits.length || !businessUnits.length) return;

  if (businessUnits && businessUnits.length) {
    dispatch(setAvailableBusinessUnits(businessUnits));

    if (businessUnits.length === 1) {
      dispatch(setBusinessUnit(businessUnits[0]));
    }
  }
};

function usePrevious<T>(value: T | undefined): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value || undefined;
  });
  return ref.current;
}

export { useUserRoles, useUserName, useDynamicRoutes, usePrevious };
